import { useCallback, useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import {
  Mail, Users, Send, MailCheck, MousePointerClick, TriangleAlert, Ban,
  TrendingUp, TrendingDown, Sparkles, Gauge, UserCheck, UserMinus, ClipboardList, Server,
  Download, Filter, ArrowDown, ShieldCheck,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const C = { gold: "#B58A45", walnut: "#4B3B31", taupe: "#B6A895", green: "#9CAE92", rose: "#CDA6A0" };
const SOURCE_COLOR = { "sunday-reset": C.gold, "life-audit": C.walnut, home: C.taupe, other: C.green };
const RANGES = [
  { key: "7", label: "7 days" }, { key: "30", label: "30 days" },
  { key: "90", label: "90 days" }, { key: "all", label: "All time" }, { key: "custom", label: "Custom" },
];
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtNum = (n) => (n == null ? "—" : Math.abs(n) >= 1000 ? (n / 1000).toFixed(1) + "K" : `${n}`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };
const titleCase = (s) => (s ? s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : s);
const tooltipStyle = { borderRadius: 12, border: "1px solid rgba(32,32,32,0.10)", background: "#FAF8F3", fontSize: 12, boxShadow: "0 8px 24px rgba(32,32,32,0.10)" };

const Toggle = ({ options, value, onChange, tp }) => (
  <div className="inline-flex flex-wrap gap-1 rounded-xl bg-secondary/60 p-1">
    {options.map((o) => (
      <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${tp}-${o.key}`}
        className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${value === o.key ? "bg-[hsl(var(--gold))] text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
        {o.label}
      </button>
    ))}
  </div>
);

const Delta = ({ v }) => {
  if (v == null) return null;
  const Icon = v >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`mt-1 inline-flex items-center gap-1 text-xs ${v >= 0 ? "text-[hsl(var(--gold))]" : "text-destructive"}`}>
      <Icon size={12} />{v >= 0 ? "+" : ""}{v}%<span className="text-muted-foreground"> vs prev</span>
    </span>
  );
};

const Kpi = ({ label, value, icon: Icon, sub, delta, testid }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      {Icon && <Icon size={16} className="text-[hsl(var(--gold))]" />}
    </div>
    <p className="mt-3 font-display text-3xl leading-none tabular-nums">{value}</p>
    {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
    {delta != null ? <Delta v={delta} /> : null}
  </Card>
);

const Section = ({ icon: Icon, title, subtitle, badge, children }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-2">
      <Icon size={20} className="text-[hsl(var(--gold))]" />
      <h3 className="font-display text-xl">{title}</h3>
      {badge}
    </div>
    {subtitle && <p className="-mt-2 text-sm text-muted-foreground">{subtitle}</p>}
    {children}
  </section>
);

const ChartCard = ({ title, subtitle, children, right }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div><h4 className="font-display text-lg">{title}</h4>{subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}</div>
      {right}
    </div>
    {children}
  </Card>
);

const ShareBars = ({ rows, keyName = "key", valName = "value", color, labelFmt = (x) => x, empty }) => {
  if (!rows || rows.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  const total = rows.reduce((a, r) => a + (r[valName] || 0), 0) || 1;
  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const share = Math.round(((r[valName] || 0) / total) * 100);
        return (
          <div key={r[keyName]} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="truncate text-foreground">{labelFmt(r[keyName])}</span>
              <span className="tabular-nums text-muted-foreground">{fmtNum(r[valName])} · {share}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-secondary/70">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, share)}%`, background: color || C.gold }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProviderError = ({ msg }) => (
  <Card className="flex items-center gap-3 border-destructive/30 bg-destructive/5 p-4">
    <TriangleAlert size={18} className="text-destructive" />
    <p className="text-sm text-muted-foreground">Couldn't load this provider{msg ? `: ${msg}` : "."}</p>
  </Card>
);

const AlertsBanner = ({ alerts }) => {
  if (!alerts || alerts.length === 0) {
    return (
      <Card className="flex items-center gap-3 border-border bg-secondary/30 p-4" data-testid="bi-email-alerts-clear">
        <ShieldCheck size={18} className="text-[hsl(var(--gold))]" />
        <p className="text-sm text-muted-foreground">Deliverability looks healthy — no bounce or spam concerns right now.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="bi-email-alerts">
      {alerts.map((a) => {
        const high = a.level === "high";
        return (
          <Card key={a.metric} data-testid={`bi-email-alert-${a.metric}`}
            className={`flex items-start gap-3 p-4 ${high ? "border-destructive/40 bg-destructive/5" : "border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5"}`}>
            <TriangleAlert size={18} className={high ? "text-destructive" : "text-[hsl(var(--gold))]"} />
            <div>
              <p className="text-sm font-medium text-foreground">{a.title}</p>
              <p className="text-sm text-muted-foreground">{a.message}</p>
            </div>
            <Badge variant="secondary" className={`ml-auto shrink-0 ${high ? "text-destructive" : "text-[hsl(var(--gold))]"}`}>
              {high ? "Needs attention" : "Keep an eye"}
            </Badge>
          </Card>
        );
      })}
    </div>
  );
};

const Funnel = ({ funnel }) => {
  if (!funnel?.ok || !funnel.stages?.length) return null;
  const top = funnel.stages[0]?.count || 0;
  return (
    <ChartCard title="Audience funnel" subtitle="From site sign-up into your MailerLite welcome flow"
      right={funnel.welcome_open_rate != null ? (
        <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]">
          {funnel.welcome_open_rate}% welcome open rate
        </Badge>
      ) : null}>
      <div className="space-y-4" data-testid="bi-email-funnel">
        {funnel.stages.map((st, i) => {
          const pct = top ? Math.round((st.count / top) * 100) : 0;
          const width = Math.max(6, Math.min(100, pct));
          return (
            <div key={st.key}>
              {i > 0 && (
                <div className="mb-2 flex items-center gap-1 pl-1 text-xs text-muted-foreground">
                  <ArrowDown size={12} className="text-[hsl(var(--gold))]" />{top ? `${pct}% of sign-ups` : ""}
                </div>
              )}
              <div className="rounded-xl border border-border bg-secondary/30 p-1.5" data-testid={`funnel-stage-${st.key}`}>
                <div className="flex items-center justify-between rounded-lg px-4 py-3"
                  style={{ width: `${width}%`, minWidth: "min(100%, 220px)", background: `linear-gradient(90deg, ${C.gold}22, ${C.gold}0d)` }}>
                  <div>
                    <p className="text-sm font-medium text-foreground">{st.label}</p>
                    <p className="text-xs text-muted-foreground">{st.sub}</p>
                  </div>
                  <span className="ml-4 font-display text-2xl tabular-nums text-foreground">{fmtNum(st.count)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
};

export const BIEmail = () => {
  const [range, setRange] = useState("all");
  const [cs, setCs] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); });
  const [ce, setCe] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (range === "custom" && (!cs || !ce)) return;
    setLoading(true);
    const params = range === "custom" ? { range, start: cs, end: ce } : { range };
    api.get("/superadmin/bi/email", { params })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load email analytics."))
      .finally(() => setLoading(false));
  }, [range, cs, ce]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = range === "custom" ? { range, start: cs, end: ce } : { range };
      const res = await api.get("/superadmin/bi/email/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `email_audience_${range}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export ready.");
    } catch {
      toast.error("Could not export CSV.");
    }
  };

  const nl = data?.newsletter;
  const ml = data?.mailerlite;
  const ms = data?.mailersend;

  return (
    <div className="space-y-10" data-testid="bi-email">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Mail size={20} className="text-[hsl(var(--gold))]" />
            <h2 className="font-display text-2xl">Email & Audience</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Live data from your newsletter, MailerLite, and MailerSend.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle options={RANGES} value={range} onChange={setRange} tp="bi-email-range" />
          <Button variant="outline" className="rounded-xl" onClick={exportCsv} data-testid="bi-email-export-csv">
            <Download size={15} className="mr-1.5" />Export CSV
          </Button>
        </div>
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-secondary/30 p-3">
          <label className="space-y-1"><span className="text-xs text-muted-foreground">From</span>
            <Input type="date" value={cs} max={ce} onChange={(e) => setCs(e.target.value)} className="h-9 rounded-lg" data-testid="bi-email-start" /></label>
          <label className="space-y-1"><span className="text-xs text-muted-foreground">To</span>
            <Input type="date" value={ce} min={cs} max={todayISO()} onChange={(e) => setCe(e.target.value)} className="h-9 rounded-lg" data-testid="bi-email-end" /></label>
        </div>
      )}

      {loading && !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-[var(--radius)] bg-secondary/60" />)}
        </div>
      ) : (
        <>
          {/* ---------------- Deliverability alerts ---------------- */}
          <AlertsBanner alerts={data?.alerts} />

          {/* ---------------- Audience funnel ---------------- */}
          <Funnel funnel={data?.funnel} />

          {/* ---------------- Newsletter ---------------- */}
          <Section icon={Users} title="Newsletter" subtitle="Sign-ups captured on your site (all sources).">
            {!nl?.ok ? <ProviderError msg={nl?.error} /> : (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi testid="nl-total" label="Total subscribers" value={fmtNum(nl.kpis.total)} icon={Users} />
                  <Kpi testid="nl-new" label="New this period" value={fmtNum(nl.kpis.new)} icon={Sparkles} delta={nl.kpis.new_delta} />
                  <Kpi testid="nl-active" label="Active" value={fmtNum(nl.kpis.active)} icon={UserCheck} />
                  <Kpi testid="nl-unsub" label="Unsubscribed" value={fmtNum(nl.kpis.unsubscribed)} icon={UserMinus} sub={`${fmtNum(nl.kpis.bounced)} bounced`} />
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <ChartCard title="Subscriber growth" subtitle="New sign-ups per day">
                    {nl.series?.length ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={nl.series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                          <defs><linearGradient id="gNl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.gold} stopOpacity={0.35} /><stop offset="100%" stopColor={C.gold} stopOpacity={0.02} /></linearGradient></defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6459" }} minTickGap={28} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: "#6b6459" }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
                          <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDate} />
                          <Area type="monotone" dataKey="subscribers" name="Sign-ups" stroke={C.gold} strokeWidth={2} fill="url(#gNl)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : <p className="py-10 text-center text-sm text-muted-foreground">No sign-ups in this period yet.</p>}
                  </ChartCard>
                  <ChartCard title="By source" subtitle="Where sign-ups came from">
                    <ShareBars rows={nl.by_source} color={C.gold} labelFmt={titleCase} empty="No sign-ups in this period yet." />
                    <div className="mt-6 border-t border-border pt-4">
                      <h5 className="mb-3 text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Status</h5>
                      <ShareBars rows={nl.by_status} color={C.walnut} labelFmt={titleCase} empty="No subscribers yet." />
                    </div>
                  </ChartCard>
                </div>
                <ChartCard title="Life Audit score spread" subtitle="How audit-takers scored (0–100)">
                  {nl.score_spread?.some((b) => b.count > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={nl.score_spread} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                        <XAxis dataKey="band" tick={{ fontSize: 12, fill: "#6b6459" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#6b6459" }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(181,138,69,0.06)" }} />
                        <Bar dataKey="count" name="People" radius={[8, 8, 0, 0]} fill={C.gold} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="py-10 text-center text-sm text-muted-foreground">No Life Audit results captured yet.</p>}
                </ChartCard>
              </>
            )}
          </Section>

          {/* ---------------- MailerLite ---------------- */}
          <Section icon={Server} title="MailerLite" subtitle="Your audience & campaigns (current snapshot)."
            badge={ml?.ok ? <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]">Connected</Badge> : null}>
            {!ml?.ok ? <ProviderError msg={ml?.error} /> : (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi testid="ml-active" label="Active subscribers" value={fmtNum(ml.kpis.active)} icon={Users} sub={ml.kpis.group_name ? `${ml.kpis.group_name}: ${fmtNum(ml.kpis.group_size)}` : ""} />
                  <Kpi testid="ml-open" label="Open rate" value={`${ml.kpis.open_rate}%`} icon={MailCheck} sub="group lifetime" />
                  <Kpi testid="ml-click" label="Click rate" value={`${ml.kpis.click_rate}%`} icon={MousePointerClick} sub="group lifetime" />
                  <Kpi testid="ml-unsub" label="Unsubscribed" value={fmtNum(ml.kpis.unsubscribed)} icon={UserMinus} sub={`${fmtNum(ml.kpis.bounced)} bounced · ${fmtNum(ml.kpis.unconfirmed)} unconfirmed`} />
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <ChartCard title="Groups" subtitle="Audience segments">
                    <ShareBars rows={ml.groups} keyName="name" valName="active" color={C.walnut} empty="No groups found." />
                  </ChartCard>
                  <ChartCard title="Campaigns" subtitle="Sent in this period">
                    {ml.campaigns?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-[0.08em] text-muted-foreground">
                            <th className="pb-2 pr-3 font-medium">Campaign</th><th className="pb-2 pr-3 text-right font-medium">Sent</th><th className="pb-2 pr-3 text-right font-medium">Open</th><th className="pb-2 text-right font-medium">Click</th>
                          </tr></thead>
                          <tbody>
                            {ml.campaigns.map((c, i) => (
                              <tr key={`${c.name}-${c.date}-${i}`} className="border-b border-border/60 last:border-0">
                                <td className="py-2.5 pr-3 font-medium text-foreground">{c.name}</td>
                                <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(c.sent)}</td>
                                <td className="py-2.5 pr-3 text-right tabular-nums text-[hsl(var(--gold))]">{c.open_rate}%</td>
                                <td className="py-2.5 text-right tabular-nums">{c.click_rate}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <p className="py-10 text-center text-sm text-muted-foreground">No campaigns sent in this period.</p>}
                  </ChartCard>
                </div>
              </>
            )}
          </Section>

          {/* ---------------- MailerSend ---------------- */}
          <Section icon={Send} title="MailerSend" subtitle="Transactional emails — your Life Audit results & reminders."
            badge={ms?.ok ? <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]">Connected</Badge> : null}>
            {!ms?.ok ? <ProviderError msg={ms?.error} /> : (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi testid="ms-sent" label="Sent" value={fmtNum(ms.kpis.sent)} icon={Send} delta={ms.kpis.sent_delta} />
                  <Kpi testid="ms-delivered" label="Delivered" value={fmtNum(ms.kpis.delivered)} icon={MailCheck} sub={`${ms.kpis.delivery_rate}% delivery`} />
                  <Kpi testid="ms-opened" label="Opened" value={fmtNum(ms.kpis.opened)} icon={Mail} sub={`${ms.kpis.open_rate}% open`} delta={ms.kpis.opened_delta} />
                  <Kpi testid="ms-clicked" label="Clicked" value={fmtNum(ms.kpis.clicked)} icon={MousePointerClick} sub={`${ms.kpis.click_rate}% click`} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi testid="ms-bounced" label="Bounced" value={fmtNum(ms.kpis.bounced)} icon={TriangleAlert} />
                  <Kpi testid="ms-spam" label="Spam complaints" value={fmtNum(ms.kpis.spam)} icon={Ban} />
                  <Card className="p-5 border-border shadow-[var(--shadow-sm)] sm:col-span-2" data-testid="ms-quota">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Monthly quota</span>
                      <Gauge size={16} className="text-[hsl(var(--gold))]" />
                    </div>
                    {ms.quota?.limit != null ? (
                      <>
                        <p className="mt-3 font-display text-2xl leading-none tabular-nums">
                          {fmtNum((ms.quota.limit || 0) - (ms.quota.remaining || 0))} <span className="text-base text-muted-foreground">/ {fmtNum(ms.quota.limit)} used</span>
                        </p>
                        <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary/70">
                          <div className="h-full rounded-full bg-[hsl(var(--gold))]" style={{ width: `${Math.min(100, Math.round(((ms.quota.limit - ms.quota.remaining) / (ms.quota.limit || 1)) * 100))}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">{fmtNum(ms.quota.remaining)} remaining · resets {ms.quota.reset ? new Date(ms.quota.reset).toLocaleDateString() : "monthly"}</p>
                      </>
                    ) : <p className="mt-3 text-sm text-muted-foreground">Quota unavailable.</p>}
                  </Card>
                </div>
                <ChartCard title="Delivery over time" subtitle="Sent, delivered & opened per day">
                  {ms.series?.some((r) => r.sent > 0) ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={ms.series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                        <defs><linearGradient id="gMs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.gold} stopOpacity={0.35} /><stop offset="100%" stopColor={C.gold} stopOpacity={0.02} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6459" }} minTickGap={28} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#6b6459" }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDate} />
                        <Area type="monotone" dataKey="delivered" name="Delivered" stroke={C.walnut} strokeWidth={1.5} fillOpacity={0} />
                        <Area type="monotone" dataKey="sent" name="Sent" stroke={C.gold} strokeWidth={2} fill="url(#gMs)" />
                        <Area type="monotone" dataKey="opened" name="Opened" stroke={C.taupe} strokeWidth={1.5} fillOpacity={0} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <p className="py-10 text-center text-sm text-muted-foreground">No transactional emails sent in this period yet.</p>}
                </ChartCard>
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
};
